import axios from 'axios'
import { showMessage } from '../context/MessageContext';

// Get the base URL from environment
// In local dev (devlocal mode), use proxy (/api)
// In other environments (dev, demo, staging, production), use the full base URL from env
const isLocal = import.meta.env.MODE === 'devlocal'
const API_BASE_URL = isLocal ? '/api' : (import.meta.env.VITE_API_BASE_URL || '/api/v1')

// Log environment + base URL for debugging
const envName = import.meta.env.VITE_ENV || import.meta.env.MODE || process.env.NODE_ENV
console.log("🌍 Environment:", envName)
console.log("🔎 API Base URL:", API_BASE_URL)
console.log("📱 App Name:", import.meta.env.VITE_APP_NAME || 'Setu Link')

const API = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false
})

// attach token
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  const superAdminToken = localStorage.getItem('superAdminToken')
  const isLoginRoute = typeof config.url === 'string' && (config.url.includes('/auth/login') || config.url.includes('/superadmin/auth/login'))
  const isSuperAdminRoute = typeof config.url === 'string' && config.url.includes('/superadmin')
  
  // Use superAdminToken for superadmin routes, otherwise use regular token
  if (isSuperAdminRoute && superAdminToken && !isLoginRoute) {
    config.headers.Authorization = `${superAdminToken}`
  } else if (token && !isLoginRoute && !isSuperAdminRoute) {
    config.headers.Authorization = `${token}`
  }
  return config
})

// Response interceptor for auth errors
API.interceptors.response.use(
  response => {
    if (response.status === 200 && response.data?.message) {
      showMessage(response.data.message, 'success');  // show success message
    }
    return response;
  },
  error => {
    if (error.response) {
      const status = error.response.status;
      const msg = error.response.data?.message || 'An error occurred.';
      // Remove token and user data
      if(status === 401){
        const isSuperAdminRoute = error.config?.url?.includes('/superadmin')
        if (isSuperAdminRoute) {
          localStorage.removeItem('superAdminToken');
          localStorage.removeItem('superAdmin');
          window.location.href = '/superadmin/login';
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/';
        }
      }
      if (status === 400 || status === 500 || (status !== 401 && status >= 400)) {
        showMessage(msg, 'error');
      }
    } else {
      // Network or unknown errors
      showMessage('Network error or server not reachable', 'error');
    }
    return Promise.reject(error);
  }
);

// ---- Auth ----
export const loginApi = (payload) => API.post('/auth/login', payload)
export const logoutApi = () => API.get('/auth/logout')
export const changePasswordApi = (payload) => API.post('/auth/change-password', payload)

export const getDashboardDataApi = (params={}) => API.get('/dashboard', { params })

// ---- User ----
export const getProfileApi = () => API.get('/user')
export const getUserListApi = (params={}) => API.get('/users', { params })

// ---- Beats ----
export const getBeatsApi = (params={}) => API.get('/beats', { params })

// ---- customers ----
export const getCustomersApi = (params={}) => API.get('/customers', { params })

// ---- Vouchers ----
export const createVoucherApi = (payload) => API.post('/vouchers', payload)
export const getVouchersApi = (params={}) => API.get('/vouchers', { params })
export const getVouchersDownloadApi = (params={}) => API.get('/vouchers/download', { params })
export const getVouchersCustomerLedgerApi = (params={}) => API.get('/vouchers/customer-ledger', { params })
export const getCompaniesApi = (params={}) => API.get('/vouchers/companies', { params })

// ---- Receipt ----
export const saveReceiptApi = (payload) => API.post('/receipts', payload)
export const getReceiptApi = (params={}) => API.get('/receipts', { params })
export const getReceiptSummaryApi = (params={}) => API.get('/receipts/summary', { params })
export const getReceiptSummaryDownloadApi = (params={}) => API.get('/receipts/summary/download', { params })
export const receiptBillApproveApi = (params={}) => API.put('/receipts/approve/'+params.allocationId, { params })
export const approveBulkReceiptApi = (payload) => API.post('/receipts/bulk-approve', payload)
export const getReceiptDetailApi = (params={}) => API.get('/receipts/detail/'+params.receiptId)
export const updateReceiptDetailApi = (payload) => API.put('/receipts/'+payload.receiptId, payload)
export const getReceiptReportApi = (params={}) => API.get('/receipts/report', { params })

// ---- Stock Register ----
export const getStockRegisterApi = (params={}) => API.get('/stock-registers', { params })
export const saveStockRegisterApi = (payload) => API.post('/stock-registers', payload)

// ---- Rent Register ----
export const getRentRegisterApi = (params={}) => API.get('/rent-registers', { params })
export const saveRentRegisterApi = (payload) => API.post('/rent-registers', payload)

// ---- Party Wise Statement ----
export const getPartyStatementApi = (params={}) => API.get('/party-statements', { params })
export const savePartyStatementApi = (payload) => API.post('/party-statements', payload)

// ---- Gatepass ----
export const getGatepassApi = (params={}) => API.get('/gatepasses', { params })
export const getGatepassDetailApi = (id) => API.get('/gatepasses/' + id)
export const saveGatepassApi = (payload) => API.post('/gatepasses', payload)
export const updateGatepassApi = (id, payload) => API.put('/gatepasses/' + id, payload)
export const deleteGatepassApi = (id) => API.delete('/gatepasses/' + id)

// ---- Organization ----
export const getOrganizationSettingsApi = (params = {}) => API.get('/organization/settings', { params })

// ---- SuperAdmin ----
export const superAdminLoginApi = (payload) => API.post('/superadmin/auth/login', payload)
export const superAdminLogoutApi = () => API.get('/superadmin/auth/logout')
export const getSuperAdminDashboardApi = () => API.get('/superadmin/dashboard')
export const getSuperAdminOrganizationsApi = (params={}) => API.get('/superadmin/organizations', { params })
export const getSuperAdminOrganizationApi = (organizationId) => API.get(`/superadmin/organizations/${organizationId}`)
export const createSuperAdminOrganizationApi = (payload) => API.post('/superadmin/organizations', payload)
export const createSuperAdminOrganizationDatabaseApi = (organizationId) => API.post(`/superadmin/organizations/${organizationId}/create-database`)
export const updateSuperAdminOrganizationApi = (organizationId, payload) => API.put(`/superadmin/organizations/${organizationId}`, payload)
export const deleteSuperAdminOrganizationApi = (organizationId) => API.delete(`/superadmin/organizations/${organizationId}`)
export const getSuperAdminCompaniesApi = (organizationId, params={}) => API.get(`/superadmin/organizations/${organizationId}/companies`, { params })
export const createSuperAdminCompanyApi = (organizationId, payload) => API.post(`/superadmin/organizations/${organizationId}/companies`, payload)
export const updateSuperAdminCompanyApi = (organizationId, companyId, payload) => API.put(`/superadmin/organizations/${organizationId}/companies/${companyId}`, payload)
export const deleteSuperAdminCompanyApi = (organizationId, companyId) => API.delete(`/superadmin/organizations/${organizationId}/companies/${companyId}`)
export const getAllCompaniesApi = (params={}) => API.get(`/superadmin/companies`, { params })
export const getAllUsersApi = (params={}) => API.get(`/superadmin/users`, { params })
export const getSuperAdminUsersApi = (organizationId, params={}) => API.get(`/superadmin/organizations/${organizationId}/users`, { params })
export const createSuperAdminUserApi = (organizationId, payload) => API.post(`/superadmin/organizations/${organizationId}/users`, payload)
export const updateSuperAdminUserApi = (organizationId, userId, payload) => API.put(`/superadmin/organizations/${organizationId}/users/${userId}`, payload)
export const deleteSuperAdminUserApi = (organizationId, userId) => API.delete(`/superadmin/organizations/${organizationId}/users/${userId}`)


export default API
